import "./heic-converter.css";
import { fileListToArray } from "./file-list-core.js";
import {
  IMAGE_LIMITS,
  outputDetails,
  outputFileName,
  settingsKey,
  validateHeicFile
} from "./heic-converter-core.js";
import { createOperationResult } from "./operation-result.js";

const root = document.querySelector("[data-heic-converter]");

if (root) {
  const ids = [
    "heicStatus", "heicUploadPanel", "heicDropZone", "chooseHeicButton", "heicFileInput",
    "uploadOutputFormat", "heicWorkspace", "heicCount", "heicProgress",
    "clearHeicFiles", "convertAgain", "downloadConverted", "heicList", "outputFormat",
    "jpegQualityField", "jpegQuality", "jpegQualityOutput"
  ];
  const elements = Object.fromEntries(ids.map(id => [id, document.getElementById(id)]));
  const state = {
    items: [],
    nextId: 1,
    generation: 1,
    worker: null,
    jobs: new Map(),
    nextJob: 1,
    queue: Promise.resolve()
  };
  const operationResult = createOperationResult(root, elements.heicWorkspace);

  function showStatus(message) {
    elements.heicStatus.textContent = message;
    elements.heicStatus.hidden = !message;
  }

  function clearStatus() {
    elements.heicStatus.hidden = true;
    elements.heicStatus.textContent = "";
  }

  function currentSettings() {
    return {
      format: elements.outputFormat.value,
      quality: Number(elements.jpegQuality.value)
    };
  }

  function openWorkspace() {
    elements.heicUploadPanel.hidden = true;
    elements.heicWorkspace.hidden = false;
    root.classList.add("is-converting");
    document.body.classList.add("heic-converter-active");
  }

  function closeWorkspace() {
    elements.heicWorkspace.hidden = true;
    elements.heicUploadPanel.hidden = false;
    root.classList.remove("is-converting");
    document.body.classList.remove("heic-converter-active");
  }

  async function addFiles(fileList) {
    const files = fileListToArray(fileList);
    if (!files.length) return;
    if (files.length !== 1) {
      showStatus("Please choose exactly one HEIC or HEIF file.");
      elements.heicFileInput.value = "";
      return;
    }
    clearStatus();
    elements.outputFormat.value = elements.uploadOutputFormat.value;
    updateFormatControls();
    state.generation += 1;
    state.items.forEach(cleanupResult);
    const file = files[0];
    const item = {
      id: `heic-${state.nextId++}`,
      name: file.name,
      size: file.size,
      file,
      status: "pending",
      resultBlob: null,
      resultUrl: null,
      width: 0,
      height: 0,
      error: null,
      completedKey: null
    };
    state.items = [item];

    if (state.items.length) openWorkspace();
    render();
    elements.heicFileInput.value = "";
    await new Promise(requestAnimationFrame);
    scheduleConversions([item]);
  }

  function getWorker() {
    if (state.worker) return state.worker;
    const worker = new Worker("/dist/heic-worker.js", { type: "module" });
    worker.addEventListener("message", event => {
      const pending = state.jobs.get(event.data.jobId);
      if (!pending) return;
      state.jobs.delete(event.data.jobId);
      if (event.data.ok) pending.resolve(event.data.blob);
      else pending.reject(new Error(event.data.error));
    });
    worker.addEventListener("error", event => {
      for (const pending of state.jobs.values()) pending.reject(new Error(event.message || "HEIC worker failed."));
      state.jobs.clear();
      state.worker?.terminate();
      state.worker = null;
    });
    state.worker = worker;
    return worker;
  }

  function workerConvert(file, settings) {
    const jobId = state.nextJob++;
    const details = outputDetails(settings.format);
    return new Promise((resolve, reject) => {
      state.jobs.set(jobId, { resolve, reject });
      getWorker().postMessage({ jobId, blob: file, type: details.mime, quality: settings.quality / 100 });
    });
  }

  function scheduleConversions(items) {
    const generation = state.generation;
    const settings = currentSettings();
    state.queue = state.queue.then(async () => {
      for (const item of items) {
        if (generation !== state.generation || !state.items.includes(item)) continue;
        await convertItem(item, settings, generation);
      }
    });
  }

  async function convertItem(item, settings, generation) {
    cleanupResult(item);
    item.status = "converting";
    item.error = null;
    render();
    try {
      const header = new Uint8Array(await item.file.slice(0, 20).arrayBuffer());
      const validationError = validateHeicFile(item.file, header);
      if (validationError) throw new Error(validationError);
      const resultBlob = await workerConvert(item.file, settings);
      if (generation !== state.generation || !state.items.includes(item)) return;
      const bitmap = await createImageBitmap(resultBlob, { imageOrientation: "from-image" });
      if (bitmap.width * bitmap.height > IMAGE_LIMITS.pixels) {
        bitmap.close();
        throw new Error("Decoded image exceeds the 64 megapixel limit.");
      }
      item.width = bitmap.width;
      item.height = bitmap.height;
      bitmap.close();
      item.resultBlob = resultBlob;
      item.resultUrl = URL.createObjectURL(resultBlob);
      item.completedKey = settingsKey(settings.format, settings.quality);
      item.status = "ready";
    } catch (error) {
      if (generation !== state.generation || !state.items.includes(item)) return;
      item.status = "error";
      item.error = error?.message || "Conversion failed.";
    }
    render();
    if (item.status === "ready") {
      operationResult.show({
        blob: item.resultBlob,
        fileName: outputFileName(item.name, settings.format),
        message: "Your image was converted successfully.",
        details: [`${item.width} × ${item.height} px`, `Converted to ${settings.format}`],
        downloadLabel: "Download image"
      });
    }
  }

  function cleanupResult(item) {
    if (item.resultUrl) URL.revokeObjectURL(item.resultUrl);
    item.resultBlob = null;
    item.resultUrl = null;
  }

  function render() {
    elements.heicCount.textContent = String(state.items.length);
    elements.heicList.replaceChildren();
    state.items.forEach(item => elements.heicList.append(createCard(item)));
    const ready = state.items.filter(item => item.status === "ready").length;
    const working = state.items.filter(item => ["pending", "converting"].includes(item.status)).length;
    const stale = state.items.filter(item => item.status === "stale").length;
    elements.heicProgress.textContent = working ? "Converting…" : stale ? "Settings changed—convert again" : ready ? "Ready to download" : "Conversion failed";
    elements.downloadConverted.disabled = ready !== 1;
    elements.convertAgain.hidden = stale === 0;
    if (!state.items.length) closeWorkspace();
  }

  function createCard(item) {
    const card = document.createElement("article");
    card.className = `heic-card${["pending", "converting", "stale"].includes(item.status) ? " is-pending" : ""}`;
    const preview = document.createElement("div");
    preview.className = "heic-preview";
    if (item.resultUrl) {
      const image = document.createElement("img");
      image.src = item.resultUrl;
      image.alt = "";
      preview.append(image);
    } else {
      const label = document.createElement("span");
      label.textContent = item.status === "error" ? "Conversion failed" : item.status === "stale" ? "Ready to reconvert" : "Converting…";
      preview.append(label);
    }

    const details = document.createElement("div");
    details.className = "heic-card-details";
    const name = document.createElement("strong");
    name.textContent = item.name;
    name.title = item.name;
    const meta = document.createElement("span");
    if (item.status === "ready") meta.textContent = `${item.width} × ${item.height} px · ${formatBytes(item.resultBlob.size)}`;
    else if (item.status === "error") { meta.textContent = item.error; meta.className = "heic-card-error"; }
    else meta.textContent = item.status === "stale" ? "Settings changed" : "Processing…";
    details.append(name, meta);

    const actions = document.createElement("div");
    actions.className = "heic-card-actions";
    const primary = document.createElement("button");
    primary.type = "button";
    primary.textContent = item.status === "error" ? "Retry" : "Ready";
    primary.disabled = item.status !== "error";
    primary.addEventListener("click", () => retryItem(item));
    const remove = document.createElement("button");
    remove.type = "button";
    remove.textContent = "Remove";
    remove.addEventListener("click", () => removeItem(item));
    actions.append(primary, remove);
    card.append(preview, details, actions);
    return card;
  }

  function formatBytes(bytes) {
    return bytes < 1024 * 1024 ? `${Math.max(1, Math.round(bytes / 1024))} KB` : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  function retryItem(item) {
    item.status = "pending";
    render();
    scheduleConversions([item]);
  }

  function removeItem(item) {
    cleanupResult(item);
    const index = state.items.indexOf(item);
    if (index >= 0) state.items.splice(index, 1);
    render();
  }

  function markResultsStale() {
    state.generation += 1;
    for (const item of state.items) {
      if (item.status === "error") continue;
      cleanupResult(item);
      item.status = "stale";
      item.completedKey = null;
    }
    render();
  }

  function reconvertAll() {
    state.generation += 1;
    const candidates = state.items.filter(item => item.status !== "error");
    candidates.forEach(item => { cleanupResult(item); item.status = "pending"; });
    render();
    scheduleConversions(candidates);
  }

  function updateFormatControls() {
    const png = elements.outputFormat.value === "PNG";
    elements.jpegQualityField.hidden = png;
    elements.uploadOutputFormat.value = elements.outputFormat.value;
  }

  function clearAll() {
    if (state.items.length && !window.confirm("Remove this file and conversion result?")) return;
    state.generation += 1;
    state.items.forEach(cleanupResult);
    state.items = [];
    clearStatus();
    render();
  }

  elements.chooseHeicButton.addEventListener("click", event => { event.stopPropagation(); elements.heicFileInput.click(); });
  elements.heicFileInput.addEventListener("change", () => addFiles(elements.heicFileInput.files));
  elements.heicDropZone.addEventListener("click", event => { if (!event.target.closest("button, select")) elements.heicFileInput.click(); });
  elements.heicDropZone.addEventListener("keydown", event => {
    if (event.key === "Enter" || event.key === " ") { event.preventDefault(); elements.heicFileInput.click(); }
  });
  for (const name of ["dragenter", "dragover"]) elements.heicDropZone.addEventListener(name, event => { event.preventDefault(); elements.heicDropZone.classList.add("is-dragging"); });
  for (const name of ["dragleave", "drop"]) elements.heicDropZone.addEventListener(name, event => { event.preventDefault(); elements.heicDropZone.classList.remove("is-dragging"); });
  elements.heicDropZone.addEventListener("drop", event => addFiles(event.dataTransfer.files));
  elements.clearHeicFiles.addEventListener("click", clearAll);
  elements.convertAgain.addEventListener("click", reconvertAll);
  elements.outputFormat.addEventListener("change", () => { updateFormatControls(); markResultsStale(); });
  elements.jpegQuality.addEventListener("input", () => { elements.jpegQualityOutput.value = elements.jpegQuality.value; });
  elements.jpegQuality.addEventListener("change", markResultsStale);
  document.querySelectorAll("[data-heic-back]").forEach(link => link.addEventListener("click", event => {
    if (state.items.length && !window.confirm("Leave this tool and discard all conversion results?")) event.preventDefault();
  }));
  window.addEventListener("beforeunload", event => {
    if (!state.items.length || operationResult.isVisible) return;
    event.preventDefault();
    event.returnValue = "";
  });
  window.addEventListener("pagehide", () => {
    operationResult.dispose();
    state.items.forEach(cleanupResult);
    state.worker?.terminate();
  });
}
